import { useState, useEffect } from "react";
import AsyncSelect from "react-select/async";
import useFetchProduct from "../../../products/useFetchProduct";
import useAdminBundle from "../useAdminBundle";

function BundleForm({ onSetActiveTab, resetForm }) {
  const [form, setForm] = useState({
    title: "",
    description: "",
    startDate: "",
    endDate: "",
    products: [],
    discountType: "percentage",
    discountValue: 0,
  });

  const { createBundle, isCreatingBundle } = useAdminBundle();

  const [productSearch, setProductSearch] = useState("");
  const { products, isGettingProducts } = useFetchProduct({
    search: productSearch,
  });

  // Reset form after successful creation (not edit)
  useEffect(() => {
    if (resetForm) {
      setForm({
        title: "",
        description: "",
        startDate: "",
        endDate: "",
        products: [],
        discountType: "percentage",
        discountValue: 0,
      });
    }
  }, [resetForm]);

  const handleProductChange = (idx, field, value) => {
    setForm((prev) => {
      const products = [...prev.products];
      products[idx] = { ...products[idx], [field]: value };
      return { ...prev, products };
    });
  };

  const addProduct = () => {
    setForm((prev) => ({
      ...prev,
      products: [...prev.products, { product: "", quantity: 1 }],
    }));
  };
  const removeProduct = (idx) => {
    setForm((prev) => ({
      ...prev,
      products: prev.products.filter((_, i) => i !== idx),
    }));
  };

  // Map products to options for AsyncSelect
  const productOptions = products?.data?.map((product) => ({
    value: product._id,
    label: product.name,
    product,
  }));

  const handleSubmit = (e) => {
    e.preventDefault();
    // Mark for reset after save
    setForm((f) => ({ ...f, _resetAfterSave: true }));

    createBundle(form, {
      onSuccess: () => {
        setForm({
          title: "",
          description: "",
          startDate: "",
          endDate: "",
          products: [],
          discountType: "percentage",
          discountValue: 0,
        });
      },
    });
  };

  return (
    <form
      className="space-y-4 bg-gray-50 p-4 rounded-lg border"
      onSubmit={handleSubmit}
    >
      <div>
        <label className="block text-sm font-medium">Title</label>
        <input
          className="w-full border rounded p-2"
          value={form.title}
          onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
          required
        />
      </div>
      <div>
        <label className="block text-sm font-medium">Description</label>
        <textarea
          className="w-full border rounded p-2"
          value={form.description}
          onChange={(e) =>
            setForm((f) => ({ ...f, description: e.target.value }))
          }
          required
        />
      </div>
      <div className="flex gap-4">
        <div className="flex-1">
          <label className="block text-sm font-medium">Start Date</label>
          <input
            type="date"
            className="w-full border rounded p-2"
            value={form.startDate?.slice(0, 10) || ""}
            onChange={(e) =>
              setForm((f) => ({ ...f, startDate: e.target.value }))
            }
            required
          />
        </div>
        <div className="flex-1">
          <label className="block text-sm font-medium">End Date</label>
          <input
            type="date"
            className="w-full border rounded p-2"
            value={form.endDate?.slice(0, 10) || ""}
            onChange={(e) =>
              setForm((f) => ({ ...f, endDate: e.target.value }))
            }
            required
          />
        </div>
      </div>
      <div>
        <label className="block text-sm font-medium">Products</label>
        {form?.products?.map((prod, idx) => (
          <div key={idx} className="flex gap-2 items-center mb-2">
            <AsyncSelect
              cacheOptions
              defaultOptions={productOptions}
              isLoading={isGettingProducts}
              options={productOptions}
              onInputChange={(inputValue) => {
                setProductSearch(inputValue);
                return inputValue;
              }}
              onChange={(option) =>
                handleProductChange(idx, "product", option.value)
              }
              placeholder="Search product by name..."
              value={
                productOptions.find((opt) => opt.value === prod.product) || null
              }
            />
            <input
              type="number"
              min={1}
              className="border rounded p-2 w-20"
              placeholder="Qty"
              value={prod.quantity}
              onChange={(e) =>
                handleProductChange(idx, "quantity", e.target.value)
              }
              required
            />
            <button
              type="button"
              className="text-red-500"
              onClick={() => removeProduct(idx)}
            >
              &times;
            </button>
          </div>
        ))}
        <button
          type="button"
          className="text-blue-600 underline"
          onClick={addProduct}
        >
          Add Product
        </button>
      </div>
      <div className="flex gap-4">
        <div className="flex-1">
          <label className="block text-sm font-medium">Discount Type</label>
          <select
            className="w-full border rounded p-2"
            value={form.discountType}
            onChange={(e) =>
              setForm((f) => ({ ...f, discountType: e.target.value }))
            }
          >
            <option value="percentage">Percentage</option>
            <option value="fixed">Fixed</option>
          </select>
        </div>
        <div className="flex-1">
          <label className="block text-sm font-medium">Discount Value</label>
          <input
            type="number"
            className="w-full border rounded p-2"
            value={form.discountValue}
            onChange={(e) =>
              setForm((f) => ({ ...f, discountValue: e.target.value }))
            }
            required
          />
        </div>
      </div>
      <div className="flex gap-4 justify-end">
        <button
          type="button"
          className="px-4 py-2 rounded bg-gray-200"
          onClick={() => onSetActiveTab("list")}
        >
          Cancel
        </button>
        <button
          type="submit"
          className="px-4 py-2 rounded bg-blue-600 text-white"
          disabled={isCreatingBundle}
        >
          {isCreatingBundle ? "Saving..." : "Save"}
        </button>
      </div>
    </form>
  );
}

export default BundleForm;
